import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Editor from '../Editor/Editor';
import { Edit, Close } from '@material-ui/icons';
import cx from 'classnames';
import Input, { INPUT_TYPES } from '../Input/Input';
import Label from '../Label/Label';
import Button from '../Button/Button';
import FancyToggle from '../FancyToggle/FancyToggle';
import ChannelContext from './ChannelContext';
import {
  CHANNEL_VARIANTS,
  CHANNEL_DESTINATION_LABEL,
  CONTEXT_GROUPS,
} from '../../constants';
import ResourceStatePill from '../ResourceStatePill';
import ChannelDelete from './ChannelDelete';

const { INCLUDE, EXCLUDE } = CONTEXT_GROUPS;
const { OFFICIAL, OVERRIDE } = CHANNEL_VARIANTS;

class ChannelCard extends Component {
  static propTypes = {
    channelIndex: PropTypes.number.isRequired,
    channel: PropTypes.object.isRequired,
    onInputFieldChange: PropTypes.func.isRequired,
    updateChannelField: PropTypes.func.isRequired,
    showVariantType: PropTypes.string.isRequired,
    currentVariant: PropTypes.string.isRequired,
    onVariantSwitch: PropTypes.func.isRequired,
    selectedChannelType: PropTypes.string.isRequired,
    addNewContext: PropTypes.func.isRequired,
    openingHours: PropTypes.array.isRequired,
    iconsList: PropTypes.array.isRequired,
    createOverrideVariant: PropTypes.func.isRequired,
  };

  renderIconList(props) {
    const { cx, className, isDisabled, isFocused, isSelected, children, innerRef, innerProps, getStyles, data } = props;
    const { component: IconComponent } = data;
    return (
      <div
        ref={innerRef}
        style={{
          ...getStyles('option', props),
          display: 'flex',
          alignItems: 'center',
        }}
        className={cx(
          {
            option: true,
            'option--is-disabled': isDisabled,
            'option--is-focused': isFocused,
            'option--is-selected': isSelected,
          },
          className
        )}
        {...innerProps}
      >
        <IconComponent style={{ marginRight: 10 }} />
        {children}
      </div>
    );
  }

  render() {
    const {
      channelIndex,
      channel,
      onInputFieldChange,
      updateChannelField,
      currentVariant,
      showVariantType,
      onVariantSwitch,
      selectedChannelType,
      addNewContext,
      openingHours,
      createOverrideVariant,
      iconsList,
    } = this.props;

    const showCurrentVariantFields = showVariantType === currentVariant;
    const variantData = channel[currentVariant];
    const variantPath = `${channelIndex}.${currentVariant}`;

    return (
      <div className='shadow-lg rounded mb-4'>
        <div className='flex justify-between items-center rounded p-3 bg-white'>
          <span className='text-sm font-bold tracking-wide'>
            {currentVariant === OFFICIAL
              ? 'DEFAULT CONFIG'
              : `${currentVariant} CONFIG`}
          </span>
          <div className='flex items-center'>
            <ResourceStatePill state={variantData.state} />
            {!channel.OVERRIDE && (
              <Button
                onClick={() => createOverrideVariant(channelIndex)}
                className='h-6 ml-4'>
                Create Override
              </Button>
            )}
            <ChannelDelete {...this.props} />
            <Button
              onClick={() => onVariantSwitch(currentVariant)}
              type='transparent'
              className='p-0-important shadow-none-important h-6 ml-8'>
              {showCurrentVariantFields ? (
                <Close className='text-darkGrey' />
              ) : (
                <Edit className='text-blue' />
              )}
            </Button>
          </div>
        </div>
        {showCurrentVariantFields && (
          <div
            className={cx('py-4 px-8', {
              'bg-lightBlue': currentVariant === OFFICIAL,
              'bg-lightGreen': currentVariant === OVERRIDE,
            })}>
            <FancyToggle
              id={`${variantPath}.online`}
              name={`${variantPath}.online`}
              checked={variantData.online}
              onChange={event => onInputFieldChange(event, channelIndex)}
              label='Status'
              labelClassName='w-64'
              onText='ONLINE'
              offText='OFFLINE'
              toggleWidthClassName='w-32'
            />
            <Input
              autoFocus
              inline
              labelClassName='w-64'
              id={`${channelIndex}.name`}
              name={`${channelIndex}.name`}
              label='Channel name'
              type={INPUT_TYPES.TEXT}
              value={channel.name}
              onChange={event => onInputFieldChange(event, channelIndex)}
              className='mt-4 max-w-sm'
            />
            <Input
              inline
              labelClassName='w-64'
              id={`${variantPath}.display_name`}
              name={`${variantPath}.display_name`}
              label='Channel display name'
              type={INPUT_TYPES.TEXT}
              value={variantData.display_name}
              onChange={event => onInputFieldChange(event, channelIndex)}
              className='mt-4 max-w-sm'
            />
            <Input
              inline
              labelClassName='w-64'
              id={`${variantPath}.channel_button_text`}
              name={`${variantPath}.channel_button_text`}
              label='Channel button text'
              type={INPUT_TYPES.TEXT}
              value={variantData.channel_button_text}
              onChange={event => onInputFieldChange(event, channelIndex)}
              className='mt-4 max-w-sm'
            />
            <FancyToggle
              className='mt-4'
              id={`${variantPath}.show_offline_state`}
              name={`${variantPath}.show_offline_state`}
              checked={variantData.show_offline_state}
              onChange={event => onInputFieldChange(event, channelIndex)}
              label='Show offline state'
              labelClassName='w-64'
              onText='YES'
              offText='NO'
              toggleWidthClassName='w-32'
            />
            <Input
              inline
              labelClassName='w-64'
              id={`${variantPath}.channel_down_text`}
              name={`${variantPath}.channel_down_text`}
              label='Channel button down text'
              type={INPUT_TYPES.TEXT}
              value={variantData.channel_down_text}
              onChange={event => onInputFieldChange(event, channelIndex)}
              className='mt-4 max-w-sm'
            />
            <Input
              inline
              labelClassName='w-64'
              id={`${variantPath}.destination`}
              name={`${variantPath}.destination`}
              label={CHANNEL_DESTINATION_LABEL[selectedChannelType]}
              type={INPUT_TYPES.TEXT}
              value={variantData.destination}
              onChange={event => onInputFieldChange(event, channelIndex)}
              className='mt-4'
            />
            <div className='mt-4 flex justify-between items-baseline'>
              <Label text='Channel primary description' className='w-64' />
              <Editor
                locale={this.props.language}
                sales_channel={this.props.country}
                value={variantData.primary_description}
                className='rounded shadow flex-grow'
                onChange={({ value }) =>
                  updateChannelField(
                    value,
                    `${variantPath}.primary_description`,
                    channelIndex,
                  )
                }
              />
            </div>
            <div className='mt-4 flex justify-between items-baseline'>
              <Label text='Channel secondary description' className='w-64' />
              <Editor
                locale={this.props.language}
                sales_channel={this.props.country}
                value={variantData.secondary_description}
                className='rounded shadow flex-grow'
                onChange={({ value }) =>
                  updateChannelField(
                    value,
                    `${variantPath}.secondary_description`,
                    channelIndex,
                  )
                }
              />
            </div>
            <div className='my-4 flex items-center'>
              <Label text='Opening hour' className='w-64' />
              <Select
                isClearable
                isSearchable
                menuPlacement='top'
                options={openingHours}
                name={`${variantPath}.opening_hour_id`}
                classNamePrefix='channeling_select'
                className='channeling_select w-full'
                value={this.props.getSingleSelectValue(
                  openingHours,
                  variantData.opening_hour_id,
                )}
                onChange={valueObject =>
                  updateChannelField(
                    valueObject ? valueObject.value : '',
                    `${variantPath}.opening_hour_id`,
                    channelIndex,
                  )
                }
              />
            </div>
            <div className='my-4 flex items-center'>
              <Label text='Channel Icon' className='w-64' />
              <Select
                isSearchable
                menuPlacement='top'
                components={{ Option: this.renderIconList }}
                options={iconsList}
                name={`${variantPath}.icon`}
                classNamePrefix='channeling_select'
                className='channeling_select w-full'
                value={this.props.getSingleSelectValue(
                  iconsList,
                  variantData.icon,
                )}
                onChange={valueObject =>
                  updateChannelField(
                    valueObject ? valueObject.value : '',
                    `${variantPath}.icon`,
                    channelIndex,
                  )
                }
                styles={{
                  option: (base) => ({
                    ...base,
                    height: '100%',
                  }),
                }}
              />
            </div>
            <div className='py-8 bt-2-dashed-midGrey'>
              <div className='flex items-center'>
                <span className='font-medium tracking-wide'>
                  INCLUDE CONTEXT
                </span>
                <Button
                  type='black-white'
                  className='ml-8 h-8'
                  onClick={() => addNewContext(channelIndex, INCLUDE)}>
                  Add
                </Button>
              </div>
              {Boolean(variantData[INCLUDE].length) && (
                <ul className='list-reset mt-4'>
                  {variantData[INCLUDE].map((context, contextIndex) => {
                    return (
                      <ChannelContext
                        {...this.props}
                        key={context.id}
                        contextType={INCLUDE}
                        contextIndex={contextIndex}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
            <div className='py-8 bt-2-dashed-midGrey bb-2-dashed-midGrey'>
              <div className='flex items-center'>
                <span className='font-medium tracking-wide'>
                  EXCLUDE CONTEXT
                </span>
                <Button
                  type='black-white'
                  className='ml-8 h-8'
                  onClick={() => addNewContext(channelIndex, EXCLUDE)}>
                  Add
                </Button>
              </div>
              {Boolean(variantData[EXCLUDE].length) && (
                <ul className='list-reset mt-4'>
                  {variantData[EXCLUDE].map((context, contextIndex) => {
                    return (
                      <ChannelContext
                        {...this.props}
                        key={context.id}
                        contextType={EXCLUDE}
                        contextIndex={contextIndex}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default  ChannelCard;
